import {
  PageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { Navigate } from 'react-router-dom';
import { TechDocsAddonLocations } from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { DocsLandingPage } from './DocsLandingPage';
import './readerChrome.css';
import { ReaderStylesAddon } from './ReaderStylesAddon';
import { CodeBlockAddon } from './CodeBlockAddon';
import { OnThisPageAddon } from './OnThisPageAddon';
import { ReaderSettingsAddon } from './ReaderSettingsAddon';
import { DocsNavAddon } from './DocsNavAddon';
import { DocsTreeSidebarAddon } from './tree/DocsTreeSidebarAddon';

const techDocsSearchResultListItem = techdocsPlugin
  .getExtension('search-result-list-item:techdocs')
  .override({
    factory: originalFactory =>
      originalFactory({
        params: {
          component: async () => {
            const { TechDocsSearchResultListItem } = await import(
              './TechDocsSearchResultListItem'
            );
            return TechDocsSearchResultListItem;
          },
        },
      }),
  });

/*
 * Replaces the stock TechDocs index page. `techDocsPage` is an unnamed
 * `PageBlueprint.make` on plugin `techdocs`, so it holds the id `page:techdocs`;
 * an unnamed page in a `techdocs` module takes that id and stands in for it.
 * The route ref is re-supplied from the plugin so `docsRouteRef` bindings
 * elsewhere in the portal keep resolving to `/documentation`.
 */
const docsLandingPage = PageBlueprint.make({
  params: {
    path: '/documentation',
    routeRef: techdocsPlugin.routes.root,
    title: 'Documentation',
    loader: async () => <DocsLandingPage />,
  },
});

/*
 * The reader page (`page:techdocs/reader`) is the vendor's own extension, not
 * one we replace outright — its loader wires up addons from both the
 * `techdocsAddonsApiRef` and this module's own inputs, which a full
 * `PageBlueprint.makeWithOverrides` redefinition would have to duplicate.
 * Overriding just `path` keeps that loader intact.
 */
const docsReaderPage = techdocsPlugin.getExtension('page:techdocs/reader').override({
  factory: originalFactory =>
    originalFactory({
      params: { path: '/documentation/:namespace/:kind/:name' },
    }),
});

/*
 * Moving both pages off `/docs` leaves that whole path unclaimed — any
 * already-indexed search doc, external link, or bookmark from before this
 * rename now 404s outright rather than landing on the new page. These two
 * redirect the old paths rather than relying on every reference elsewhere
 * getting updated (or reindexed) in lockstep.
 */
const docsRedirect = PageBlueprint.make({
  name: 'docs-redirect',
  params: {
    path: '/docs',
    loader: async () => <Navigate to="/documentation" replace />,
  },
});

const docsReaderRedirect = PageBlueprint.make({
  name: 'docs-reader-redirect',
  params: {
    path: '/docs/:namespace/:kind/:name',
    loader: async () => {
      const { DocsReaderRedirect } = await import('./DocsReaderRedirect');
      return <DocsReaderRedirect />;
    },
  },
});

/*
 * Reader addons. All three work on the mkdocs-material markup inside the
 * TechDocs shadow root, which no stylesheet in the app bundle can reach:
 * `ReaderStyles` injects the doc-body grammar, `CodeBlocks` adds the language
 * and copy bar the ink code surface needs, and `OnThisPage` replaces the inert
 * mkdocs table of contents with a scroll-spy rail. `DocsNav` is the phone form of
 * both rails: the reader drops its sidebars below the phone breakpoint, so the
 * page tree and the headings float over the prose as a split pill and the sheet
 * it opens.
 */
const readerStylesAddon = AddonBlueprint.make({
  name: 'reader-styles',
  params: {
    name: 'HoneReaderStyles',
    location: TechDocsAddonLocations.Content,
    component: ReaderStylesAddon,
  },
});

const codeBlockAddon = AddonBlueprint.make({
  name: 'code-blocks',
  params: {
    name: 'HoneCodeBlocks',
    location: TechDocsAddonLocations.Content,
    component: CodeBlockAddon,
  },
});

const onThisPageAddon = AddonBlueprint.make({
  name: 'on-this-page',
  params: {
    name: 'HoneOnThisPage',
    location: TechDocsAddonLocations.SecondarySidebar,
    component: OnThisPageAddon,
  },
});

/*
 * The pill and its sheet are fixed to the viewport, so they render from the
 * Subheader — app DOM — rather than the sidebar locations the two desktop rails
 * use.
 */
const docsNavAddon = AddonBlueprint.make({
  name: 'docs-nav',
  params: {
    name: 'HoneDocsNav',
    location: TechDocsAddonLocations.Subheader,
    component: DocsNavAddon,
  },
});

/*
 * A Subheader addon — the toolbar the vendor settings gear renders in — but
 * nothing there itself; see `ReaderSettingsAddon.tsx` for why it portals its
 * trigger + menu into the portal header's own icon cluster instead.
 */
const readerSettingsAddon = AddonBlueprint.make({
  name: 'reader-settings',
  params: {
    name: 'HoneReaderSettings',
    location: TechDocsAddonLocations.Subheader,
    component: ReaderSettingsAddon,
  },
});

/*
 * The whole catalog's doc tree, placed below whichever doc set's own page
 * list the reader is showing — the same tree the Search page's Tree tab
 * browses from, so every reader page carries "all documentation" with it.
 */
const docsTreeAddon = AddonBlueprint.make({
  name: 'docs-tree',
  params: {
    name: 'HoneDocsTree',
    location: TechDocsAddonLocations.PrimarySidebar,
    component: DocsTreeSidebarAddon,
  },
});

export const techdocsModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    docsLandingPage,
    docsReaderPage,
    docsRedirect,
    docsReaderRedirect,
    readerStylesAddon,
    codeBlockAddon,
    onThisPageAddon,
    docsNavAddon,
    readerSettingsAddon,
    docsTreeAddon,
    techDocsSearchResultListItem,
  ],
});
